import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import BackButton from "@/components/BackButton";
import ClosedFeature from "@/components/ClosedFeature";
import { Hotel, FileText, ArrowRight, Calendar } from "lucide-react";

export default function AccommodationChoice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cartRegId = searchParams.get("cart_reg_id") || "";
  const [dashData, setDashData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [unpaidReg, setUnpaidReg] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => {
      setDashData(r.data);
      const regs = r.data?.registrations || [];
      setUnpaidReg(regs.find(rg => rg.payment_status !== "paid") || null);
      setDataLoaded(true);
    }).catch(() => { setDataLoaded(true); });
  }, [user]);

  if (!dataLoaded) {
    return (
      <div className="min-h-screen bg-[#0B1429]">
        <Navbar />
        <div className="pt-28 pb-20 max-w-4xl mx-auto px-6 lg:px-10 space-y-4">
          <div className="h-8 w-24 bg-white/[0.03] animate-pulse rounded" />
          <div className="h-12 w-64 bg-white/[0.03] animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (dataLoaded && user?.role !== "admin") {
    const hasEventsInCart = !!unpaidReg;
    const hasPaidAcc = !!dashData?.accommodations?.some(a => a.payment_status === "paid");
    if (!hasEventsInCart && !hasPaidAcc && (dashData?.registrations || []).length === 0) {
      return (
        <ClosedFeature
          featureName="Accommodation"
          icon={Calendar}
          subtitle="SELECT EVENTS FIRST"
          badgeLabel={null}
          description="Please complete your event selection first. Once you've chosen your events, you'll be directed here to choose your accommodation."
        />
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-4xl mx-auto px-6 lg:px-10">
        <BackButton className="mb-6" />
        <div className="eyebrow mb-3">Next Step</div>
        <h1 className="font-display font-black text-4xl md:text-5xl mb-2">
          Accommodation <span className="text-[#00F0FF]">Selection</span>
        </h1>
        <p className="text-[#94A3B8] mb-10">
          International participants are required to use the official event accommodation or opt out by paying an administrative fee.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Option 1: Official Accommodation */}
          <div className="card-dark p-6 flex flex-col items-start border border-[#00F0FF]/20 hover:border-[#00F0FF]/50 transition-colors group cursor-pointer" onClick={() => navigate(`/accommodation${cartRegId ? `?cart_reg_id=${cartRegId}` : ""}`)}>
            <div className="w-12 h-12 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <Hotel className="w-6 h-6 text-[#00F0FF]" />
            </div>
            <h2 className="font-display font-bold text-2xl mb-2">Official Accommodation</h2>
            <p className="text-sm text-[#94A3B8] mb-6 flex-1">
              Book your stay at one of our partner hotels. Includes full-board meals, airport transfers, and daily venue transport.
            </p>
            <button className="btn-primary w-full justify-center">
              Browse Hotels <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>

          {/* Option 2: Administrative Fee */}
          <div className="card-dark p-6 flex flex-col items-start border border-[#2A3A5F] hover:border-white/30 transition-colors group cursor-pointer" onClick={() => navigate(`/administrative-fee${cartRegId ? `?cart_reg_id=${cartRegId}` : ""}`)}>
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <FileText className="w-6 h-6 text-white/70" />
            </div>
            <h2 className="font-display font-bold text-2xl mb-2">Administrative Fee</h2>
            <p className="text-sm text-[#94A3B8] mb-6 flex-1">
              Opt out of the official accommodation package and make your own stay arrangements. A $75 administrative fee applies.
            </p>
            <button className="btn-secondary w-full justify-center text-white">
              Pay Administrative Fee <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
